import { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { defaultScopes } from "services/authProvider";
import { signupAuthority } from "services/authProviderSignup";
import { finalizeAppLogoutRedirect, clearLogoutSessionFlags } from "utils/appLogout";
import {
  clearPendingLoginRedirectFlag,
  isPendingLoginRedirectFlagSet,
  safeMsalLoginRedirect,
  setPendingLoginRedirectFlag,
} from "utils/msalLoginRedirect";

/**
 * Sign-in, sign-up, and sign-out using a single PublicClientApplication (see MsalProvider).
 */
export const useAuth = () => {
  const { instance, inProgress } = useMsal();
  const pendingRedirectRequestRef = useRef(null);
  const flushInFlightRef = useRef(false);
  const [loginRedirectPending, setLoginRedirectPending] = useState(
    () => isPendingLoginRedirectFlagSet(),
  );

  const markLoginRedirectPending = useCallback(() => {
    setPendingLoginRedirectFlag();
    setLoginRedirectPending(true);
  }, []);

  const clearLoginRedirectPending = useCallback(() => {
    clearPendingLoginRedirectFlag();
    setLoginRedirectPending(false);
    pendingRedirectRequestRef.current = null;
  }, []);

  const cancelPendingLoginRedirect = useCallback(() => {
    flushInFlightRef.current = false;
    clearLoginRedirectPending();
  }, [clearLoginRedirectPending]);

  const flushPendingLoginRedirect = useCallback(async () => {
    const request = pendingRedirectRequestRef.current;
    if (!request || inProgress !== InteractionStatus.None || flushInFlightRef.current) {
      return;
    }

    flushInFlightRef.current = true;
    try {
      await safeMsalLoginRedirect(instance, request, { skipPendingFlag: true });
      pendingRedirectRequestRef.current = null;
    } catch {
      cancelPendingLoginRedirect();
    } finally {
      flushInFlightRef.current = false;
    }
  }, [instance, inProgress, cancelPendingLoginRedirect]);

  useEffect(() => {
    flushPendingLoginRedirect();
  }, [inProgress, flushPendingLoginRedirect]);

  const queueLoginRedirect = useCallback(
    (request) => {
      clearLogoutSessionFlags();
      pendingRedirectRequestRef.current = request;
      markLoginRedirectPending();

      if (inProgress === InteractionStatus.None) {
        flushPendingLoginRedirect();
      }
    },
    [inProgress, flushPendingLoginRedirect, markLoginRedirectPending],
  );

  const signinHandler = useCallback(() => {
    queueLoginRedirect({ scopes: defaultScopes });
  }, [queueLoginRedirect]);

  const signupHandler = useCallback(
    (pathname) => {
      const extraQueryParameters = {};
      if (pathname === "/supplier") {
        extraQueryParameters.role = "vendor";
      }
      if (pathname === "/patient") {
        extraQueryParameters.role = "patient";
      }
      queueLoginRedirect({
        scopes: defaultScopes,
        authority: signupAuthority,
        ...(Object.keys(extraQueryParameters).length > 0 && {
          extraQueryParameters,
        }),
      });
    },
    [queueLoginRedirect],
  );

  const logout = useCallback(
    (options) => {
      cancelPendingLoginRedirect();
      return finalizeAppLogoutRedirect(instance, options);
    },
    [instance, cancelPendingLoginRedirect],
  );

  return {
    instance,
    signinHandler,
    signupHandler,
    logout,
    cancelPendingLoginRedirect,
    isLoginRedirectPending: loginRedirectPending,
  };
};
